/**
 * v2's display preferences: theme, and how large the text is.
 *
 * The whole v2 UI sizes its type from the root font size (see the
 * `html[data-app='v2']` rule in styles/globals.css), because Tailwind's scale
 * is in rem. Changing one custom property therefore rescales every label,
 * table cell and heading at once - headings stay proportionally larger than
 * body text, and nothing needs per-component work.
 *
 * Theme works the same way, through the neutral ramp in styles/globals.css:
 * the whole UI re-colours from ten CSS variables, so a theme costs ten values
 * rather than a `dark:` variant on every colour class in the application.
 *
 * Stored per browser rather than on the account: both are properties of the
 * screen you are sitting at, and someone on a laptop and a large monitor
 * wants different answers on each.
 */

import { applyChartTheme } from '../../charts/tokens';

export type FontScale = 'compact' | 'normal' | 'large';

/** Browser default is 16px; compact is the one most people want on a laptop. */
const SIZES: Record<FontScale, string> = {
  compact: '14px',
  normal: '16px',
  large: '18px',
};

export const FONT_SCALE_LABELS: Record<FontScale, string> = {
  compact: 'Compact',
  normal: 'Normal',
  large: 'Large',
};

export const FONT_SCALES = Object.keys(SIZES) as FontScale[];

const KEY = 'valuator_v2_font_scale';

function isFontScale(value: unknown): value is FontScale {
  return typeof value === 'string' && value in SIZES;
}

export function getFontScale(): FontScale {
  // localStorage throws in a private window with site data blocked, and the
  // preference is a convenience - never let it stop the app rendering.
  try {
    const stored = localStorage.getItem(KEY);
    if (isFontScale(stored)) return stored;
  } catch {
    /* fall through to the default */
  }
  return 'normal';
}

export function setFontScale(scale: FontScale): void {
  try {
    localStorage.setItem(KEY, scale);
  } catch {
    /* the change still applies for this session */
  }
  applyFontScale(scale);
}

export function applyFontScale(scale: FontScale): void {
  document.documentElement.style.setProperty('--v2-font-size', SIZES[scale]);
}

export function applyStoredFontScale(): void {
  applyFontScale(getFontScale());
}


export type Theme = 'light' | 'dark' | 'sepia' | 'ledger' | 'graphite' | 'terminal' | 'contrast';

export const THEMES: Theme[] = ['light', 'sepia', 'ledger', 'contrast', 'dark', 'graphite', 'terminal'];

export const THEME_LABELS: Record<Theme, string> = {
  light: 'Light',
  dark: 'Dark',
  sepia: 'Sepia',
  ledger: 'Ledger',
  graphite: 'Graphite',
  terminal: 'Terminal',
  contrast: 'High contrast',
};

/** One line each, for the picker: what the theme is for, not what it is made of. */
export const THEME_NOTES: Record<Theme, string> = {
  light: 'Clean white paper',
  sepia: 'Warm, for reading',
  ledger: 'Green-ruled pad',
  contrast: 'Strongest ink',
  dark: 'Slate, for dim rooms',
  graphite: 'Warm charcoal',
  terminal: 'Amber on black',
};

/**
 * What each theme looks like, for its swatch in the picker: canvas, surface,
 * ink and rule, the same values as its block in globals.css. A swatch drawn
 * from the theme itself would need the theme applied to show it.
 */
export const THEME_SWATCH: Record<Theme, { canvas: string; surface: string; ink: string; rule: string }> = {
  light: { canvas: '#f4f6f9', surface: '#ffffff', ink: '#111827', rule: '#e5e7eb' },
  sepia: { canvas: '#ece2ca', surface: '#f6eeda', ink: '#2c2216', rule: '#d8c8a7' },
  ledger: { canvas: '#e0e8dc', surface: '#fafcf6', ink: '#151f17', rule: '#b8c4b3' },
  contrast: { canvas: '#e2e2e2', surface: '#ffffff', ink: '#000000', rule: '#808080' },
  dark: { canvas: '#090e1c', surface: '#0f172a', ink: '#f8fafc', rule: '#334155' },
  graphite: { canvas: '#141311', surface: '#1e1d1b', ink: '#f6f3ed', rule: '#56524d' },
  terminal: { canvas: '#060503', surface: '#0e0c08', ink: '#ffd68e', rule: '#4e4129' },
};

const THEME_KEY = 'valuator_v2_theme';

function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as string[]).includes(value);
}

export function getTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (isTheme(stored)) return stored;
  } catch {
    /* fall through to the default */
  }
  return 'light';
}

export function applyTheme(theme: Theme): void {
  // Light is the absence of an override, so the variables fall back to the
  // :root defaults rather than being restated in a third block.
  if (theme === 'light') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = theme;

  // Charts do not read the data-theme attribute; they read CSS variables that
  // Recharts resolves inside SVG fill and stroke. Writing them here is what
  // repaints every chart on a theme change - see charts/tokens.ts.
  applyChartTheme(theme);
}

export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* the change still applies for this session */
  }
  applyTheme(theme);
}

export function applyStoredTheme(): void {
  applyTheme(getTheme());
}
